import React, { useState } from "react";
import Icon from "components/AppIcon";

const risks = [
  {
    category: "Construction",
    icon: "HardHat",
    color: "text-orange-600 bg-orange-50",
    items: [
      {
        risk: "Cost overrun",
        severity: "High",
        likelihood: "Medium",
        mitigation: "Fixed-price EPC contract with performance bonds. Contingency reserve of 12% built into base case.",
        status: "Mitigated",
      },
      {
        risk: "Schedule delay",
        severity: "Medium",
        likelihood: "Medium",
        mitigation: "Liquidated damages clause in EPC contract. Buffer of 3 months in financial model.",
        status: "Mitigated",
      },
      {
        risk: "Contractor insolvency",
        severity: "High",
        likelihood: "Low",
        mitigation: "Parent company guarantee from Tier 1 contractor. Step-in rights secured.",
        status: "Monitored",
      },
    ],
  },
  {
    category: "Operational",
    icon: "Settings",
    color: "text-[#3b4c4c] bg-[rgba(59,76,76,0.07)]",
    items: [
      {
        risk: "Revenue shortfall",
        severity: "Medium",
        likelihood: "Low",
        mitigation: "Long-term offtake agreement with investment-grade counterparty covering 85% of capacity.",
        status: "Mitigated",
      },
      {
        risk: "O&M cost escalation",
        severity: "Low",
        likelihood: "Medium",
        mitigation: "Long-term O&M contract with CPI-linked pricing. Benchmarked against comparable assets.",
        status: "Mitigated",
      },
    ],
  },
  {
    category: "Regulatory",
    icon: "Scale",
    color: "text-purple-600 bg-purple-50",
    items: [
      {
        risk: "Planning permission",
        severity: "High",
        likelihood: "Low",
        mitigation: "Full planning consent obtained. Judicial review period expired without challenge.",
        status: "Resolved",
      },
      {
        risk: "Grid connection",
        severity: "Medium",
        likelihood: "Low",
        mitigation: "Grid connection agreement signed with National Grid. Capacity reserved.",
        status: "Resolved",
      },
    ],
  },
  {
    category: "Market",
    icon: "TrendingDown",
    color: "text-red-600 bg-red-50",
    items: [
      {
        risk: "Power price decline",
        severity: "Medium",
        likelihood: "Medium",
        mitigation: "15-year CfD contract provides floor price protection. Merchant tail risk modelled conservatively.",
        status: "Mitigated",
      },
      {
        risk: "Interest rate risk",
        severity: "Medium",
        likelihood: "Low",
        mitigation: "Senior debt fully hedged via interest rate swap for 10-year term.",
        status: "Mitigated",
      },
    ],
  },
];

const severityColors = {
  High: "bg-red-100 text-red-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-emerald-100 text-emerald-700",
};

const statusColors = {
  Resolved: "bg-emerald-100 text-emerald-700",
  Mitigated: "bg-blue-100 text-blue-700",
  Monitored: "bg-amber-100 text-amber-700",
  Open: "bg-red-100 text-red-700",
};

export default function KeyRisksTab() {
  const [expanded, setExpanded] = useState({ Construction: true });

  const toggle = (cat) => setExpanded((prev) => ({ ...prev, [cat]: !prev?.[cat] }));

  return (
    <div className="space-y-4">
      {/* Risk Matrix Summary */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Risk Summary Matrix</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Risks", value: "9", icon: "AlertTriangle", color: "text-foreground" },
            { label: "Resolved", value: "2", icon: "CheckCircle", color: "text-success" },
            { label: "Mitigated", value: "6", icon: "ShieldCheck", color: "text-primary" },
            { label: "Monitored", value: "1", icon: "Eye", color: "text-warning" },
          ]?.map((s) => (
            <div key={s?.label} className="p-3 rounded-md bg-muted/50 text-center">
              <Icon name={s?.icon} size={20} color={`var(--color-${s?.color?.replace("text-", "")})`} className="mx-auto mb-1" />
              <p className={`font-data text-2xl font-semibold ${s?.color}`}>{s?.value}</p>
              <p className="font-caption text-xs text-muted-foreground">{s?.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Risk Categories */}
      {risks?.map((cat) => (
        <div key={cat?.category} className="card p-0 overflow-hidden">
          <button
            onClick={() => toggle(cat?.category)}
            className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${cat?.color}`}>
                <Icon name={cat?.icon} size={18} />
              </div>
              <div className="text-left">
                <p className="font-heading text-base font-semibold text-foreground">{cat?.category} Risk</p>
                <p className="font-caption text-xs text-muted-foreground">{cat?.items?.length} risk factors identified</p>
              </div>
            </div>
            <Icon name={expanded?.[cat?.category] ? "ChevronUp" : "ChevronDown"} size={18} color="var(--color-muted-foreground)" />
          </button>

          {expanded?.[cat?.category] && (
            <div className="border-t border-border divide-y divide-border">
              {cat?.items?.map((item, i) => (
                <div key={i} className="p-4 md:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <p className="font-caption font-semibold text-foreground text-sm">{item?.risk}</p>
                    <div className="flex gap-2">
                      <span className={`text-xs font-caption font-medium px-2 py-0.5 rounded-full ${severityColors?.[item?.severity]}`}>
                        {item?.severity} Severity
                      </span>
                      <span className={`text-xs font-caption font-medium px-2 py-0.5 rounded-full ${statusColors?.[item?.status]}`}>
                        {item?.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon name="Shield" size={14} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <p className="font-caption text-sm text-muted-foreground">{item?.mitigation}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}